import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Video, VideoContextType } from '../types';
import { getVideos } from '../services/api';

const VideoContext = createContext<VideoContextType | undefined>(undefined);

export const useVideo = () => {
  const context = useContext(VideoContext);
  if (!context) {
    throw new Error('useVideo must be used within a VideoProvider');
  }
  return context;
};

interface VideoProviderProps {
  children: ReactNode;
}

export const VideoProvider: React.FC<VideoProviderProps> = ({ children }) => {
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [page, setPage] = useState(0);

  const loadVideos = useCallback(async (date: Date = selectedDate, append: boolean = false) => {
    setLoading(true);
    try {
      const from = new Date(date);
      from.setHours(0, 0, 0, 0);
      const to = new Date(date);
      to.setHours(23, 59, 59, 999);

      const newVideos = await getVideos(from.toISOString(), to.toISOString());
      if (append) {
        setVideos(prev => [...prev, ...newVideos]);
        setPage(prev => prev + 1);
      } else {
        setVideos(newVideos);
        setPage(0);
      }
      setHasMore(newVideos.length === 20); // Assuming 20 per page
    } catch (error) {
      console.error('Error loading videos:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  const handleSetSelectedDate = (date: Date) => {
    setSelectedDate(date);
    setVideos([]);
    setPage(0);
    setHasMore(true);
    loadVideos(date, false);
  };

  const value: VideoContextType = {
    videos,
    loading,
    hasMore,
    selectedDate,
    setSelectedDate: handleSetSelectedDate,
    loadVideos,
  };

  return <VideoContext.Provider value={value}>{children}</VideoContext.Provider>;
};